import { Component, inject, Injector, OnDestroy, OnInit, Signal } from '@angular/core';
import { isKnownTab, Tab } from '@modules/common/types/tab';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { MainViewService } from '@modules/common/services/main-view.service';
import { ModalService } from '@modules/modal/services/modal.service';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';
import { StateKey } from '@modules/settings/types/state-key';
import { SplitViewService } from '@modules/split-view/services/split-view.service';
import { ProjectListStateService } from '@modules/tasks/services/project-list-state.service';
import { ProjectsService } from '@modules/tasks/services/projects.service';
import { TaskingAppStateService } from '@modules/tasks/services/state.service';
import { TaskListStateService } from '@modules/tasks/services/task-list-state.service';
import { TasksService } from '@modules/tasks/services/tasks.service';
import { ListState } from '@modules/tasks/types/list-state';
import { Project } from '@modules/tasks/types/project';
import { ProjectsFilters } from '@modules/tasks/types/projects-filters';
import { Task } from '@modules/tasks/types/task';
import { TasksFilters } from '@modules/tasks/types/tasks-filters';
import { VirtualFolder } from '@modules/tasks/types/virtual-folder';
import { combineLatest, Subject } from 'rxjs';
import { distinctUntilChanged, scan, takeUntil } from 'rxjs/operators';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { toSignal } from '@angular/core/rxjs-interop';
import { Application } from '@modules/common/types/application';

@Component({
  selector: 'app-tasks-list-container',
  templateUrl: './tasks-list-container.component.html',
  styleUrls: ['./tasks-list-container.component.less'],
  standalone: false,
  providers: [
    ...TaskListStateService.providers({
      stateKey: StateKey.tasksListState,
      withAdvancedSearch: true,
    }),
    ...ProjectListStateService.providers({
      stateKey: StateKey.projectsListState,
      withAdvancedSearch: true,
    }),
  ],
})
export class TasksListContainerComponent implements OnInit, OnDestroy {
  public minimized: boolean;
  public searchQuery: string;
  public listState: ListState;
  public sidebarFilter: VirtualFolder;
  public quickProject: Project;
  public quickTask: Task;
  public loading: boolean;
  public tabs: Tab<Exclude<ListState, 'tabs'>>[] = [
    { title: 'Projects', value: 'projects' },
    { title: 'Tasks', value: 'tasks' },
  ];
  public selectedTab: Exclude<ListState, 'tabs'> = 'tasks';
  public projectsFilters: ProjectsFilters;
  public tasksFilters: TasksFilters;
  public debug: 'score' = null;
  public containerName: string | null = null;
  public taskTenantItems: ReadonlyArray<{ readonly accountId: string }> | undefined;

  private alive = new Subject<void>();

  private readonly taskListStateService = inject(TaskListStateService, { self: true });
  private readonly projectListStateService = inject(ProjectListStateService, { self: true });
  private readonly advancedSearchService = inject(AdvancedSearchService);
  private readonly tasksService = inject(TasksService);
  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);
  private readonly projectsService = inject(ProjectsService);
  // Keep the project's request owner while its tasks occupy the editor. The selected project
  // filter below decides whether this owner still applies; it never changes the app's tenant.
  private readonly openedProject$ = inject(MainViewService).getMainView().pipe(
    scan((project: Project | null, item) => item?.itemType === StitchType.PROJECT ? item as Project : project, null),
    distinctUntilChanged(),
  );

  protected readonly isAdvancedSearchApplied: Signal<boolean> = toSignal(
    this.advancedSearchService.isAdvancedSearchApplied(Application.tasking),
    { initialValue: false },
  );

  /**
   * The account zone as a SOURCE, not a one-off read (SBUX-829).
   *
   * `DisplayTimeZoneService` starts on the browser zone and is corrected to the account's once the space
   * settings load. Anything that reads it once, early, therefore captures the browser zone forever — this
   * container builds its filters in the constructor, so `timeZone()` returned `Asia/Bishkek` while the
   * service later said `Europe/Kiev`, and the day windows kept asking for the device's day. Feeding it in
   * as a stream rebuilds the filters when the real zone arrives, and again if the user changes it.
   */
  private readonly displayTimeZone$ = this.displayTimeZoneService.resolvedTimeZone$;

  constructor(
    private splitViewService: SplitViewService,
    private stateService: TaskingAppStateService,
    private modalService: ModalService,
    private searchService: AdvancedSearchService,
    private injector: Injector,
  ) {
    combineLatest([
      this.projectListStateService.value(),
      this.searchService.getState(),
      this.stateService.getVirtualFolder(),
      this.displayTimeZone$,
      this.stateService.getTeam(),
      this.stateService.getVirtualFolderReselections(),
    ])
      .pipe(takeUntil(this.alive))
      .subscribe(([list, search, folder, displayTimeZone]) => {
        // `displayTimeZone` FIRST: applyListState/applyVirtualFolder read it to reckon their day
        // windows in the account zone (SBUX-829).
        this.projectsFilters = new ProjectsFilters({ displayTimeZone })
          .applyListState(list)
          .applyAdvancedFilters(search)
          .applyVirtualFolder(folder);
      });

    combineLatest([
      this.taskListStateService.value(),
      this.searchService.getState(),
      this.stateService.getVirtualFolder(),
      this.displayTimeZone$,
      this.stateService.getTeam(),
      this.openedProject$,
      this.stateService.getVirtualFolderReselections(),
    ])
      .pipe(takeUntil(this.alive))
      .subscribe(([list, search, folder, displayTimeZone, , project]) => {
        this.taskTenantItems = project?.accountId && search.tasking.containersIds?.includes(project.id)
          ? [{ accountId: project.accountId }]
          : undefined;
        this.tasksFilters = new TasksFilters({ displayTimeZone })
          .applyListState(list)
          .applyAdvancedFilters(search)
          .applyVirtualFolder(folder);
      });
  }

  /**
   * Lifecycle
   */

  ngOnInit() {
    this.splitViewService
      .getMinimized('tasksList')
      .pipe(takeUntil(this.alive))
      .subscribe((minimized: boolean) => {
        this.minimized = minimized;
      });

    this.stateService
      .getTabs()
      .pipe(takeUntil(this.alive))
      .subscribe((state: ListState) => {
        this.listState = state;
        if (this.listState !== 'tabs') {
          this.selectedTab = this.listState;
        }
        // Unconditional, not just on the non-'tabs' branch above: a container recreated by
        // stchSpaceRerender (e.g. on a space switch) re-inits `selectedTab` to its field default, but
        // the app-state service instance survives the switch and keeps whatever tab was last published
        // — republishing here on every emission, including while folder-mapped to 'tabs', overwrites
        // that stale value as soon as this container exists instead of only once the folder next
        // changes to a concrete one.
        this.stateService.setSelectedTab(this.selectedTab);
      });

    combineLatest([this.searchService.getState(), this.stateService.getVirtualFolder()])
      .pipe(takeUntil(this.alive))
      .subscribe(([state, folder]) => {
        this.sidebarFilter = folder;
        this.containerName = state[Application.tasking]?.containerName ?? null;
        this.quickTask = Task.fromAdvancedState(state, folder);
        this.quickProject = Project.fromAdvancedState(state, folder);
      });
  }

  ngOnDestroy() {
    this.alive.next();
    this.alive.complete();
  }

  /**
   * Actions
   */

  protected handleSelectedTabChange(tab: string): void {
    if (!isKnownTab<Exclude<ListState, 'tabs'>>(tab, this.tabs)) {
      return;
    }
    this.selectedTab = tab;
    this.stateService.setSelectedTab(tab);
  }

  public handleNewTask(task = new Task()): void {
    this.stateService.seedTaskDraft(task);
    this.stateService.setMainView(task);
  }

  handleNewProject(project = new Project()) {
    this.stateService.setMainView(project);
  }

  handleDoubleClickTask(task: Task) {
    this.modalService.openFullForm(task, this.injector);
  }

  handleClickTask(task: Task) {
    this.stateService.setMainView(task);
  }

  handleDoubleClickProject(project: Project) {
    this.modalService.openFullForm(project, this.injector);
  }

  handleClickProject(project: Project) {
    this.stateService.setMainView(project);
  }

  doDebug() {
    switch (this.debug) {
      case 'score':
        this.debug = null;
        break;
      default:
        this.debug = 'score';
    }
  }
}
